import { z } from 'zod';

export const CONTACT_TYPES = ['vendor', 'customer', 'employee', 'other'] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];
export const contactTypeSchema = z.enum(CONTACT_TYPES);

/** W-9 federal tax classifications (drives 1099 eligibility). */
export const TAX_CLASSIFICATIONS = [
  'individual',
  'sole_proprietor',
  'single_member_llc',
  'partnership',
  'llc',
  's_corp',
  'c_corp',
  'trust_estate',
  'nonprofit',
] as const;
export type TaxClassification = (typeof TAX_CLASSIFICATIONS)[number];

/** Default IRS 1099-NEC reporting threshold (USD). */
export const NEC_1099_THRESHOLD = 600;

/** Standard invoice payment-term presets. A customer's default flows onto new invoices. */
export const PAYMENT_TERMS = [
  'Due on receipt',
  'Net 7',
  'Net 15',
  'Net 30',
  'Net 45',
  'Net 60',
] as const;
export type PaymentTerm = (typeof PAYMENT_TERMS)[number];

/** Resolve a terms string to a number of days from the invoice date until it is due. */
export function termsToNetDays(terms: string | null | undefined): number {
  if (!terms) return 30;
  if (/due on receipt/i.test(terms)) return 0;
  const m = terms.match(/net\s*(\d+)/i);
  if (m) return parseInt(m[1]!, 10);
  return 30;
}

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const createContactInputSchema = z.object({
  name: z.string().trim().min(1).max(200),
  contactType: contactTypeSchema,
  email: optionalText(200),
  phone: optionalText(50),
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  city: optionalText(100),
  region: optionalText(100),
  postalCode: optionalText(20),
  countryCode: optionalText(2),
  taxId: optionalText(50),
  website: optionalText(200),
  notes: optionalText(2000),
  // Empty string clears the terms (→ null) so a customer's terms can be removed.
  paymentTerms: z
    .string()
    .trim()
    .max(100)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v)),
  track1099: z.boolean().optional(),
  taxClassification: z.enum(TAX_CLASSIFICATIONS).nullable().optional(),
});
export type CreateContactInput = z.infer<typeof createContactInputSchema>;

export const updateContactInputSchema = createContactInputSchema
  .partial()
  .extend({ isActive: z.boolean().optional() });
export type UpdateContactInput = z.infer<typeof updateContactInputSchema>;

export const contactSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  contactType: contactTypeSchema,
  email: z.string().nullable(),
  phone: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string().nullable(),
  taxId: z.string().nullable(),
  website: z.string().nullable(),
  notes: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  track1099: z.boolean(),
  taxClassification: z.enum(TAX_CLASSIFICATIONS).nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Contact = z.infer<typeof contactSchema>;

// ── 1099-NEC report ────────────────────────────────────────────────────────────

export interface Nec1099Row {
  contactId: string;
  name: string;
  taxId: string | null;
  taxClassification: TaxClassification | null;
  /** Total reportable (Box 1) amount paid this year. */
  total: string;
  meetsThreshold: boolean;
  missingTaxId: boolean;
}

export interface Nec1099Report {
  year: number;
  threshold: number;
  rows: Nec1099Row[];
  /** Vendors flagged for 1099 tracking but with no reportable activity this year. */
  inactiveVendors: { contactId: string; name: string }[];
  totalReportable: string;
}
